import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { 
	Col,
	Spin,
} from 'antd';
import { 
  BarChartOutlined, 
  WarningOutlined, 
  DollarOutlined, 
  RiseOutlined,
  FilterOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  PlusOutlined,
  CloseOutlined
} from '@ant-design/icons';
import Header from '../../components/Header/Header';
import HeaderFile from '../../components/Header/HeaderFile';
import DateRangeFilter from '../../components/ui/DateRangeFilter';
// API IMPORTS - NOW ACTIVE
import { getUserToken } from '../../utils/helper';
import config from '../../config';
import axios from 'axios';

// XVSLottie imports
import { XVSLottie } from '../VoiceAssistance/VoiceAssistanceStyle';
import DashboardLoaderJson from '../../json/dashboard_loader.json';
import NanoLoaderJson from '../../json/nano_loading_withoutloop2.json';
import LoadingFiles from '../../json/loading_files.json';

// NanoDashboard Style imports
import { 
  NanoLoaderWrapper, 
  NanoXVSLottie,
  NanoHexagonContainer,
  NanoHexagon,
  NanoProgressContainer,
  NanoProgressBar,
  NanoProgressFill,
  NanoConnectionText,
  NanoRadioButton,
  NanoLottieCard
} from './NanoDashboardStyle';

// Utility Functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatNumber = (num) => {
  return new Intl.NumberFormat('en-US').format(num);
};

// StatCard Component - EXACT replica from original
const StatCard = ({ title, value, format = 'number', icon: Icon, trend, loading = false, className = '' }) => {
  const formatValue = (val) => {
    switch (format) {
      case 'currency':
        return formatCurrency(val);
      case 'percentage':
        return `${val.toFixed(1)}%`;
      default:
        return formatNumber(val);
    }
  };

  if (loading) {
    return (
      <div className={`stat-card ${className}`}>
        <div style={{ animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ height: '16px', backgroundColor: '#E5E7EB', borderRadius: '4px', width: '50%' }}></div>
            <div style={{ height: '32px', width: '32px', backgroundColor: '#E5E7EB', borderRadius: '4px' }}></div>
      </div>
          <div style={{ marginTop: '16px' }}>
            <div style={{ height: '32px', backgroundColor: '#E5E7EB', borderRadius: '4px', width: '75%' }}></div>
            <div style={{ height: '12px', backgroundColor: '#E5E7EB', borderRadius: '4px', width: '50%', marginTop: '8px' }}></div>
    </div>
  </div>
  </div>
);
  }

  return (
    <div className={`stat-card ${className}`}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ 
          fontSize: '14px', 
          fontWeight: '500', 
          color: '#6B7280',
          margin: 0,
          fontFamily: 'Inter, system-ui, sans-serif'
        }}>
          {title}
        </h3>
        {Icon && (
          <div style={{ flexShrink: 0 }}>
            <Icon style={{ 
              fontSize: '32px', 
              width: '32px',
              height: '32px',
              color: '#0E4C88' 
            }} />
  </div>
        )}
  </div>
      
      <div style={{ marginTop: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <p style={{ 
            fontSize: '24px', 
            fontWeight: '600', 
            color: '#111827',
            margin: 0,
            fontFamily: 'Inter, system-ui, sans-serif',
            lineHeight: '1.2'
          }}>
            {formatValue(value)}
          </p>
  </div>
        
        {trend && (
          <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center' }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              paddingLeft: '8px',
              paddingRight: '8px',
              paddingTop: '2px',
              paddingBottom: '2px',
              borderRadius: '9999px',
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor: trend.isPositive ? '#D1FAE5' : '#FEE2E2',
              color: trend.isPositive ? '#065F46' : '#991B1B'
            }}>
              <svg
                style={{
                  width: '12px',
                  height: '12px',
                  marginRight: '4px',
                  color: trend.isPositive ? '#10B981' : '#EF4444'
                }}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                {trend.isPositive ? (
                  <path
                    fillRule="evenodd"
                    d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                ) : (
                  <path
                    fillRule="evenodd"
                    d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                )}
              </svg>
              {Math.abs(trend.value)}%
            </span>
            <span style={{ 
              marginLeft: '8px', 
              color: '#6B7280',
              fontSize: '14px',
              fontFamily: 'Inter, system-ui, sans-serif'
            }}>
              {trend.label}
            </span>
          </div>
        )}
      </div>
  </div>
);
};

// Custom hooks with API calls
const useDenialReasonsAnalytics = (filters) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);
  const filtersRef = useRef();
  
  // Store current filters in ref to avoid dependency issues
  filtersRef.current = filters;

  const fetchData = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const currentFilters = filtersRef.current;
      
      // API CALL - NOW ACTIVE with nano-denial prefix
      const params = new URLSearchParams();
      if (currentFilters?.startDate) params.append('startDate', currentFilters.startDate);
      if (currentFilters?.endDate) params.append('endDate', currentFilters.endDate);
      if (currentFilters?.provider) params.append('provider', currentFilters.provider);
      if (currentFilters?.carrier) params.append('carrier', currentFilters.carrier);
      if (currentFilters?.category) params.append('category', currentFilters.category);
      
      const queryString = params.toString();
      const apiUrl = `${config.api.base_url}/nano-denial/analytics/denial-reasons${queryString ? `?${queryString}` : ''}`;
      
      console.log('🔄 Fetching analytics:', queryString || 'no filters'); // Simplified debug log
      
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: 'Bearer ' + getUserToken(),
        },
      });
      
      if (isMountedRef.current) {
        console.log('✅ Analytics loaded successfully');
        setData(response.data);
        setIsLoading(false);
      }
      
    } catch (err) {
      console.error('Failed to fetch denial reasons analytics:', err);
      if (isMountedRef.current) {
        setError(err);
        setIsLoading(false);
      }
    }
  }, []); // Empty dependency array - stable function

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // Use individual filter properties as dependencies to trigger re-fetch with debounce
  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    isMountedRef.current = true;
    
    // Debounce API calls to prevent too many rapid requests
    const debounceTimer = setTimeout(() => {
      if (isMountedRef.current) {
        fetchData();
      }
    }, 300); // 300ms debounce
    
    return () => {
      clearTimeout(debounceTimer);
      isMountedRef.current = false;
    };
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    filters?.startDate, 
    filters?.endDate, 
    filters?.provider, 
    filters?.carrier, 
    filters?.category
  ]); // We intentionally exclude fetchData to prevent infinite loops

  return { data, isLoading, error, refetch };
};

const useAnalyticsFilterOptions = (filters = {}) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isMountedRef = useRef(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!isMountedRef.current) return;
      
      try {
        setIsLoading(true);

        // Build query parameters based on current filters
        // Only pass upstream filters to avoid self-filtering
        const params = new URLSearchParams();
        if (filters?.provider) params.append('provider', filters.provider);
        // Don't pass carrier filter to avoid self-filtering of carriers
        // Don't pass category filter to avoid self-filtering of categories
        
        const queryString = params.toString();
        const apiUrl = `${config.api.base_url}/nano-denial/analytics/filter-options${queryString ? `?${queryString}` : ''}`;

        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: 'Bearer ' + getUserToken(),
          },
        });

        if (isMountedRef.current) {
          console.log('📋 Filter options loaded with filters:', filters);
          setData(response.data);
          setIsLoading(false);
        }

      } catch (err) {
        console.error('Failed to fetch filter options:', err);
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    isMountedRef.current = true;
    fetchData();

    return () => {
      isMountedRef.current = false;
    };
  }, [filters?.provider]); // Only re-fetch when provider changes

  return { data, isLoading };
};

// Dashboard Trends Hook - Fetches trends data
const useDashboardTrends = (filters) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);
  const filtersRef = useRef();
  
  // Store current filters in ref to avoid dependency issues
  filtersRef.current = filters;

  const fetchData = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const currentFilters = filtersRef.current;
      
      // API CALL - Dashboard trends endpoint
      const params = new URLSearchParams();
      if (currentFilters?.startDate) params.append('startDate', currentFilters.startDate);
      if (currentFilters?.endDate) params.append('endDate', currentFilters.endDate);
      if (currentFilters?.provider) params.append('provider', currentFilters.provider);
      if (currentFilters?.carrier) params.append('carrier', currentFilters.carrier);
      if (currentFilters?.category) params.append('category', currentFilters.category);
      
      const queryString = params.toString();
      const apiUrl = `${config.api.base_url}/nano-denial/dashboard/trends${queryString ? `?${queryString}` : ''}`;
      
      console.log('🔄 Fetching trends:', queryString || 'no filters');
      
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: 'Bearer ' + getUserToken(),
        },
      });
      
      if (isMountedRef.current) {
        console.log('✅ Trends loaded successfully');
        setData(response.data);
        setIsLoading(false);
      }
      
    } catch (err) {
      console.error('Failed to fetch dashboard trends:', err);
      if (isMountedRef.current) {
        setError(err);
        setIsLoading(false);
      }
    }
  }, []); // Empty dependency array - stable function

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // Use individual filter properties as dependencies to trigger re-fetch with debounce
  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    isMountedRef.current = true;
    
    // Debounce API calls to prevent too many rapid requests
    const debounceTimer = setTimeout(() => {
      if (isMountedRef.current) {
        fetchData();
      }
    }, 300); // 300ms debounce
    
    return () => {
      clearTimeout(debounceTimer);
      isMountedRef.current = false;
    };
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    filters?.startDate, 
    filters?.endDate, 
    filters?.provider, 
    filters?.carrier, 
    filters?.category
  ]); // We intentionally exclude fetchData to prevent infinite loops

  return { data, isLoading, error, refetch };
};

// TrendsChart Component - Matching Dashboard.tsx reference design  
const TrendsChart = ({ data }) => {
  const [crosshairPosition, setCrosshairPosition] = useState(null);
  const [hoveredData, setHoveredData] = useState(null);
  
  if (!data || data.length === 0) {
    return (
      <div style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#6B7280' }}>
          <div style={{ fontSize: '48px', marginBottom: '8px' }}>📊</div>
          <p style={{ fontSize: '14px', fontFamily: 'Inter, system-ui, sans-serif' }}>No trend data available</p>
        </div>
      </div>
    );
  }

  // Sort data by date to ensure proper chronological order
  const sortedData = [...data].sort((a, b) => {
    const dateA = new Date(a._id?.year || 2024, (a._id?.month || 1) - 1);
    const dateB = new Date(b._id?.year || 2024, (b._id?.month || 1) - 1);
    return dateA.getTime() - dateB.getTime();
  });

  const maxAmount = Math.max(...sortedData.map(d => d.totalAmount || 0), 1); // Ensure minimum value
  const maxCount = Math.max(...sortedData.map(d => d.count || 0), 1); // Ensure minimum value
  
  // Debug: Log the data for line calculation
  console.log('Line data:', sortedData.map((item, index) => {
    const x = (100 / sortedData.length) * index + (100 / sortedData.length) * 0.5;
    const y = 100 - ((item.totalAmount || 0) / maxAmount) * 100;
    return { x: `${x}%`, y: `${y}%`, totalAmount: item.totalAmount, maxAmount };
  }));
  
  // Debug: Log the line segment data
  const lineSegments = sortedData.map((item, index) => {
    if (index === 0) return null;
    const segmentWidth = 100 / sortedData.length;
    const x1 = segmentWidth * (index - 1) + segmentWidth * 0.5;
    const y1 = 100 - ((sortedData[index - 1].totalAmount || 0) / maxAmount) * 100;
    const x2 = segmentWidth * index + segmentWidth * 0.5;
    const y2 = 100 - ((item.totalAmount || 0) / maxAmount) * 100;
    return { x1: `${x1}%`, y1: `${y1}%`, x2: `${x2}%`, y2: `${y2}%`, totalAmount: item.totalAmount };
  }).filter(Boolean);
  console.log('Line segments:', lineSegments);

  // Generate month labels from data (like "Nov", "Mar", "Jul")
  const formatMonthLabel = (item) => {
    if (item._id && item._id.year && item._id.month) {
      const date = new Date(item._id.year, item._id.month - 1);
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = item._id.year;
      return `${month} ${year}`;
    }
    return '';
  };

  // Function to find the closest data point to mouse position
  const findClosestDataPoint = (mouseX, containerWidth) => {
    const segmentWidth = containerWidth / sortedData.length;
    const segmentIndex = Math.floor(mouseX / segmentWidth);
    const clampedIndex = Math.max(0, Math.min(segmentIndex, sortedData.length - 1));
    return { data: sortedData[clampedIndex], index: clampedIndex };
  };

  return (
    <div style={{ height: '400px', position: 'relative', padding: '20px 0', width: '100%' }}>
      {/* Left Y-axis (Amount) */}
      <div style={{ 
        position: 'absolute', 
        left: '0', 
        top: '20px', 
        bottom: '50px', 
        width: '100px', 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'space-between', 
        fontSize: '12px', 
        color: '#6B7280',
        fontFamily: 'Inter, system-ui, sans-serif'
      }}>
        <span>${(maxAmount / 1000).toFixed(0)},000.00</span>
        <span>${(maxAmount * 0.75 / 1000).toFixed(0)},000.00</span>
        <span>${(maxAmount * 0.5 / 1000).toFixed(0)},000.00</span>
        <span>${(maxAmount * 0.25 / 1000).toFixed(0)},000.00</span>
        <span>$0.00</span>
      </div>
      
      {/* Right Y-axis (Count) */}
      <div style={{ 
        position: 'absolute', 
        right: '0', 
        top: '20px', 
        bottom: '50px', 
        width: '70px', 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'space-between', 
        fontSize: '12px', 
        color: '#6B7280',
        textAlign: 'right',
        fontFamily: 'Inter, system-ui, sans-serif'
      }}>
        <span>{maxCount}</span>
        <span>{Math.floor(maxCount * 0.75)}</span>
        <span>{Math.floor(maxCount * 0.5)}</span>
        <span>{Math.floor(maxCount * 0.25)}</span>
        <span>0</span>
      </div>

      {/* Chart Container */}
      <div 
        className="chart-container" 
        style={{ 
        marginLeft: '100px', 
        marginRight: '70px', 
        marginBottom: '50px', 
        height: '300px', 
        position: 'relative',
        borderLeft: '1px solid #E5E7EB',
        borderBottom: '1px solid #E5E7EB'
        }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const mouseX = e.clientX - rect.left;
          const mouseY = e.clientY - rect.top;
          
          // Find closest data point
          const { data: closestData, index } = findClosestDataPoint(mouseX, rect.width);
          
          setCrosshairPosition({ x: mouseX, y: mouseY });
          setHoveredData({ data: closestData, index });
        }}
        onMouseLeave={() => {
          setCrosshairPosition(null);
          setHoveredData(null);
        }}
      >
        
        {/* Crosshair Lines */}
        {crosshairPosition && (
          <>
            {/* Vertical line */}
            <div style={{
              position: 'absolute',
              left: `${crosshairPosition.x}px`,
              top: '0',
              bottom: '0',
              width: '1px',
              backgroundColor: '#0E4C88',
              borderStyle: 'dashed',
              borderWidth: '1px',
              borderColor: '#0E4C88',
              opacity: 0.7,
              pointerEvents: 'none',
              zIndex: 10
            }} />
            {/* Horizontal line */}
            <div style={{
              position: 'absolute',
              left: '0',
              right: '0',
              top: `${crosshairPosition.y}px`,
              height: '1px',
              backgroundColor: '#0E4C88',
              borderStyle: 'dashed',
              borderWidth: '1px',
              borderColor: '#0E4C88',
              opacity: 0.7,
              pointerEvents: 'none',
              zIndex: 10
            }} />
            
            {/* Crosshair Tooltip */}
            {hoveredData && !document.getElementById('chart-tooltip') && (
              <div style={{
                position: 'absolute',
                left: `${crosshairPosition.x + 10}px`,
                top: `${crosshairPosition.y - 120}px`,
                background: 'linear-gradient(135deg, #FFFFFF, #F8FAFC)',
                color: '#1F2937',
                padding: '16px',
                borderRadius: '12px',
                fontSize: '13px',
                fontFamily: 'Inter, system-ui, sans-serif',
                pointerEvents: 'none',
                zIndex: 9999,
                minWidth: '280px',
                boxShadow: '0 8px 32px rgba(14, 76, 136, 0.15)',
                border: '1px solid #E5E7EB',
                backdropFilter: 'blur(8px)',
                transform: 'translateX(-50%)'
              }}>
                {(() => {
                  const item = hoveredData.data;
                  const index = hoveredData.index;
                  const avgAmount = item.count ? (item.totalAmount || 0) / item.count : 0;
                  const trend = item.totalAmount > (item.count * 100) ? '↗️ Increasing' : '↘️ Decreasing';
                  const performance = item.totalAmount > maxAmount * 0.8 ? 'High' : item.totalAmount > maxAmount * 0.5 ? 'Medium' : 'Low';
                  
                  return (
                    <>
                      <div style={{marginBottom: '12px', textAlign: 'center'}}>
                        <div style={{fontWeight: '700', fontSize: '14px', color: '#0E4C88', marginBottom: '4px'}}>
                          {formatMonthLabel(item)}
                        </div>
                        <div style={{fontSize: '11px', color: '#6B7280'}}>Crosshair Analysis</div>
                      </div>
                      
                      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px'}}>
                        <div style={{background: '#F3F4F6', padding: '8px', borderRadius: '6px', textAlign: 'center'}}>
                          <div style={{fontSize: '11px', color: '#6B7280', marginBottom: '2px'}}>💰 Total Amount</div>
                          <div style={{fontWeight: '600', fontSize: '12px', color: '#D22027'}}>{formatCurrency(item.totalAmount || 0)}</div>
                        </div>
                        <div style={{background: '#F3F4F6', padding: '8px', borderRadius: '6px', textAlign: 'center'}}>
                          <div style={{fontSize: '11px', color: '#6B7280', marginBottom: '2px'}}>📊 Denial Count</div>
                          <div style={{fontWeight: '600', fontSize: '12px', color: '#0E4C88'}}>{formatNumber(item.count || 0)}</div>
                        </div>
                        <div style={{background: '#F3F4F6', padding: '8px', borderRadius: '6px', textAlign: 'center'}}>
                          <div style={{fontSize: '11px', color: '#6B7280', marginBottom: '2px'}}>📈 Avg per Denial</div>
                          <div style={{fontWeight: '600', fontSize: '12px', color: '#D22027'}}>{formatCurrency(avgAmount)}</div>
                        </div>
                        <div style={{background: '#F3F4F6', padding: '8px', borderRadius: '6px', textAlign: 'center'}}>
                          <div style={{fontSize: '11px', color: '#6B7280', marginBottom: '2px'}}>🎯 Performance</div>
                          <div style={{fontWeight: '600', fontSize: '12px', color: performance === 'High' ? '#10B981' : performance === 'Medium' ? '#F59E0B' : '#EF4444'}}>{performance}</div>
                        </div>
                      </div>
                      
                      <div style={{borderTop: '1px solid #E5E7EB', paddingTop: '8px'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px'}}>
                          <span style={{fontSize: '11px', color: '#6B7280'}}>Trend:</span>
                          <span style={{fontWeight: '600', fontSize: '12px', color: trend.includes('Increasing') ? '#10B981' : '#EF4444'}}>{trend}</span>
                        </div>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                          <span style={{fontSize: '11px', color: '#6B7280'}}>Rank:</span>
                          <span style={{fontWeight: '600', fontSize: '12px', color: '#0E4C88'}}>#{index + 1} of {sortedData.length}</span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </>
        )}
        
        {/* Grid lines */}
        <div style={{ position: 'absolute', inset: '0' }}>
          {[0, 25, 50, 75, 100].map(percent => (
            <div key={percent} style={{
              position: 'absolute',
              top: `${100 - percent}%`,
              left: '0',
              right: '0',
              height: '1px',
              backgroundColor: percent === 0 ? '#E5E7EB' : '#F3F4F6'
            }} />
          ))}
        </div>

        {/* Data visualization */}
        <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
          {/* Gradient definitions */}
          <defs>
            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#0E4C88" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#0E4C88" stopOpacity="0.05" />
            </linearGradient>
          </defs>
          
          {/* Red bars for count */}
          {sortedData.map((item, index) => {
            const barWidth = Math.max((100 / sortedData.length) * 0.3, 0.5); // Reduced width to prevent overlap
            const barX = (100 / sortedData.length) * index + (100 / sortedData.length) * 0.35; // Adjusted positioning
            const barHeight = ((item.count || 0) / maxCount) * 100;
            
            return (
              <g key={`bar-${index}`}>
                <rect
                  x={`${barX}%`}
                  y={`${100 - barHeight}%`}
                  width={`${barWidth}%`}
                  height={`${barHeight}%`}
                  fill="#EF4444"
                  opacity="0.7"
                  rx="1"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    // Remove any existing tooltip first
                    const existingTooltip = document.getElementById('chart-tooltip');
                    if (existingTooltip) existingTooltip.remove();
                    
                    // Hide crosshair when hovering over specific elements
                    setCrosshairPosition(null);
                    setHoveredData(null);
                    
                    const tooltip = document.createElement('div');
                    tooltip.style.cssText = `
                      position: absolute;
                      background: linear-gradient(135deg, #FFFFFF, #F8FAFC);
                      color: #1F2937;
                      padding: 16px;
                      border-radius: 12px;
                      font-size: 13px;
                      font-family: Inter, system-ui, sans-serif;
                      pointer-events: none;
                      z-index: 9999;
                      min-width: 280px;
                      box-shadow: 0 8px 32px rgba(14, 76, 136, 0.15);
                      border: 1px solid #E5E7EB;
                      backdrop-filter: blur(8px);
                    `;
                    
                    // Calculate trend and performance indicators
                    const avgAmount = item.count ? (item.totalAmount || 0) / item.count : 0;
                    const trend = item.totalAmount > (item.count * 100) ? '↗️ Increasing' : '↘️ Decreasing';
                    const performance = item.totalAmount > maxAmount * 0.8 ? 'High' : item.totalAmount > maxAmount * 0.5 ? 'Medium' : 'Low';
                    
                    tooltip.innerHTML = `
                      <div style="margin-bottom: 12px; text-align: center;">
                        <div style="font-weight: 700; font-size: 14px; color: #0E4C88; margin-bottom: 4px;">${formatMonthLabel(item)}</div>
                        <div style="font-size: 11px; color: #6B7280;">Data Point Analysis</div>
                      </div>
                      
                      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px;">
                        <div style="background: #F3F4F6; padding: 8px; border-radius: 6px; text-align: center;">
                          <div style="font-size: 11px; color: #6B7280; margin-bottom: 2px;">💰 Total Amount</div>
                          <div style="font-weight: 600; font-size: 12px; color: #D22027;">${formatCurrency(item.totalAmount || 0)}</div>
                        </div>
                        <div style="background: #F3F4F6; padding: 8px; border-radius: 6px; text-align: center;">
                          <div style="font-size: 11px; color: #6B7280; margin-bottom: 2px;">📊 Denial Count</div>
                          <div style="font-weight: 600; font-size: 12px; color: #0E4C88;">${formatNumber(item.count || 0)}</div>
                        </div>
                        <div style="background: #F3F4F6; padding: 8px; border-radius: 6px; text-align: center;">
                          <div style="font-size: 11px; color: #6B7280; margin-bottom: 2px;">📈 Avg per Denial</div>
                          <div style="font-weight: 600; font-size: 12px; color: #D22027;">${formatCurrency(avgAmount)}</div>
                        </div>
                        <div style="background: #F3F4F6; padding: 8px; border-radius: 6px; text-align: center;">
                          <div style="font-size: 11px; color: #6B7280; margin-bottom: 2px;">🎯 Performance</div>
                          <div style="font-weight: 600; font-size: 12px; color: ${performance === 'High' ? '#10B981' : performance === 'Medium' ? '#F59E0B' : '#EF4444'};">${performance}</div>
                        </div>
                      </div>
                      
                      <div style="border-top: 1px solid #E5E7EB; padding-top: 8px;">
                        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
                          <span style="font-size: 11px; color: #6B7280;">Trend:</span>
                          <span style="font-weight: 600; font-size: 12px; color: ${trend.includes('Increasing') ? '#10B981' : '#EF4444'};">${trend}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; align-items: center;">
                          <span style="font-size: 11px; color: #6B7280;">Rank:</span>
                          <span style="font-weight: 600; font-size: 12px; color: #0E4C88;">#${index + 1} of ${sortedData.length}</span>
                        </div>
                      </div>
                    `;
                    tooltip.id = 'chart-tooltip';
                    
                    // Position tooltip relative to the chart container
                    const chartContainer = e.target.closest('.chart-container') || e.target.closest('svg').parentElement;
                    chartContainer.style.position = 'relative';
                    chartContainer.appendChild(tooltip);
                    
                    const rect = e.target.getBoundingClientRect();
                    const containerRect = chartContainer.getBoundingClientRect();
                    
                    // Position tooltip near the mouse but within the chart area
                    const tooltipX = rect.left - containerRect.left + rect.width/2;
                    const tooltipY = rect.top - containerRect.top - 120;
                    
                    tooltip.style.left = `${Math.max(10, Math.min(containerRect.width - tooltip.offsetWidth - 10, tooltipX))}px`;
                    tooltip.style.top = `${Math.max(10, tooltipY)}px`;
                  }}
                  onMouseLeave={() => {
                    const tooltip = document.getElementById('chart-tooltip');
                    if (tooltip) tooltip.remove();
                  }}
                />
              </g>
            );
          })}
          
                    {/* Area fill under the line */}
          <path
            d={(() => {
              const points = sortedData.map((item, index) => {
                const segmentWidth = 100 / sortedData.length;
                const x = segmentWidth * index + segmentWidth * 0.5;
              const y = 100 - ((item.totalAmount || 0) / maxAmount) * 100;
                return `${x}% ${y}%`;
              });
              
              // Create a closed path that goes from first point to last point, then down to bottom, then back to first point
              const pathData = [
                `M ${points[0]}`,
                ...points.slice(1).map(point => `L ${point}`),
                `L ${points[points.length - 1].split(' ')[0]} 100%`,
                `L ${points[0].split(' ')[0]} 100%`,
                'Z'
              ].join(' ');
              
              return pathData;
            })()}
            fill="url(#areaGradient)"
            opacity="0.1"
          />
          
          {/* Line graph for total amount - using individual line segments */}
          {sortedData.map((item, index) => {
            if (index === 0) return null; // Skip first point as we need pairs
            
            const segmentWidth = 100 / sortedData.length;
            const x1 = segmentWidth * (index - 1) + segmentWidth * 0.5;
            const y1 = 100 - ((sortedData[index - 1].totalAmount || 0) / maxAmount) * 100;
            const x2 = segmentWidth * index + segmentWidth * 0.5;
            const y2 = 100 - ((item.totalAmount || 0) / maxAmount) * 100;
            
            return (
              <line
                key={`line-${index}`}
                x1={`${x1}%`}
                y1={`${y1}%`}
                x2={`${x2}%`}
                y2={`${y2}%`}
            stroke="#0E4C88"
            strokeWidth="2.5"
            strokeLinecap="round"
                opacity="1"
                style={{ 
                  filter: 'drop-shadow(0 1px 2px rgba(14, 76, 136, 0.2))',
                  zIndex: 2
                }}
              />
            );
          })}
          
          {/* Blue dots on line */}
          {sortedData.map((item, index) => {
            const x = (100 / sortedData.length) * index + (100 / sortedData.length) * 0.5;
            const y = 100 - ((item.totalAmount || 0) / maxAmount) * 100;
            
            return (
              <circle
                key={`dot-${index}`}
                cx={`${x}%`}
                cy={`${y}%`}
                r="4"
                fill="#0E4C88"
                stroke="white"
                strokeWidth="2"
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  // Remove any existing tooltip first
                  const existingTooltip = document.getElementById('chart-tooltip');
                  if (existingTooltip) existingTooltip.remove();
                  
                  // Hide crosshair when hovering over specific elements
                  setCrosshairPosition(null);
                  setHoveredData(null);
                  
                  const tooltip = document.createElement('div');
                  tooltip.style.cssText = `
                    position: absolute;
                    background: linear-gradient(135deg, #FFFFFF, #F8FAFC);
                    color: #1F2937;
                    padding: 16px;
                    border-radius: 12px;
                    font-size: 13px;
                    font-family: Inter, system-ui, sans-serif;
                    pointer-events: none;
                    z-index: 9999;
                    min-width: 280px;
                    box-shadow: 0 8px 32px rgba(14, 76, 136, 0.15);
                    border: 1px solid #E5E7EB;
                    backdrop-filter: blur(8px);
                  `;
                  
                  // Calculate trend and performance indicators
                  const avgAmount = item.count ? (item.totalAmount || 0) / item.count : 0;
                  const trend = item.totalAmount > (item.count * 100) ? '↗️ Increasing' : '↘️ Decreasing';
                  const performance = item.totalAmount > maxAmount * 0.8 ? 'High' : item.totalAmount > maxAmount * 0.5 ? 'Medium' : 'Low';
                  
                  tooltip.innerHTML = `
                    <div style="margin-bottom: 12px; text-align: center;">
                      <div style="font-weight: 700; font-size: 14px; color: #0E4C88; margin-bottom: 4px;">${formatMonthLabel(item)}</div>
                      <div style="font-size: 11px; color: #6B7280;">Data Point Analysis</div>
                    </div>
                    
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px;">
                      <div style="background: #F3F4F6; padding: 8px; border-radius: 6px; text-align: center;">
                        <div style="font-size: 11px; color: #6B7280; margin-bottom: 2px;">💰 Total Amount</div>
                        <div style="font-weight: 600; font-size: 12px; color: #D22027;">${formatCurrency(item.totalAmount || 0)}</div>
                      </div>
                      <div style="background: #F3F4F6; padding: 8px; border-radius: 6px; text-align: center;">
                        <div style="font-size: 11px; color: #6B7280; margin-bottom: 2px;">📊 Denial Count</div>
                        <div style="font-weight: 600; font-size: 12px; color: #0E4C88;">${formatNumber(item.count || 0)}</div>
                      </div>
                      <div style="background: #F3F4F6; padding: 8px; border-radius: 6px; text-align: center;">
                        <div style="font-size: 11px; color: #6B7280; margin-bottom: 2px;">📈 Avg per Denial</div>
                        <div style="font-weight: 600; font-size: 12px; color: #D22027;">${formatCurrency(avgAmount)}</div>
                      </div>
                      <div style="background: #F3F4F6; padding: 8px; border-radius: 6px; text-align: center;">
                        <div style="font-size: 11px; color: #6B7280; margin-bottom: 2px;">🎯 Performance</div>
                        <div style="font-weight: 600; font-size: 12px; color: ${performance === 'High' ? '#10B981' : performance === 'Medium' ? '#F59E0B' : '#EF4444'};">${performance}</div>
                      </div>
                    </div>
                    
                    <div style="border-top: 1px solid #E5E7EB; padding-top: 8px;">
                      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
                        <span style="font-size: 11px; color: #6B7280;">Trend:</span>
                        <span style="font-weight: 600; font-size: 12px; color: ${trend.includes('Increasing') ? '#10B981' : '#EF4444'};">${trend}</span>
                      </div>
                      <div style="display: flex; justify-content: space-between; align-items: center;">
                        <span style="font-size: 11px; color: #6B7280;">Rank:</span>
                        <span style="font-weight: 600; font-size: 12px; color: #0E4C88;">#${index + 1} of ${sortedData.length}</span>
                      </div>
                    </div>
                  `;
                  tooltip.id = 'chart-tooltip';
                  
                  // Position tooltip relative to the chart container
                  const chartContainer = e.target.closest('.chart-container') || e.target.closest('svg').parentElement;
                  chartContainer.style.position = 'relative';
                  chartContainer.appendChild(tooltip);
                  
                  const rect = e.target.getBoundingClientRect();
                  const containerRect = chartContainer.getBoundingClientRect();
                  
                  // Position tooltip near the mouse but within the chart area
                  const tooltipX = rect.left - containerRect.left + rect.width/2;
                  const tooltipY = rect.top - containerRect.top - 120;
                  
                  tooltip.style.left = `${Math.max(10, Math.min(containerRect.width - tooltip.offsetWidth - 10, tooltipX))}px`;
                  tooltip.style.top = `${Math.max(10, tooltipY)}px`;
                }}
                onMouseLeave={() => {
                  const tooltip = document.getElementById('chart-tooltip');
                  if (tooltip) tooltip.remove();
                }}
              />
            );
          })}
        </svg>
      </div>
      
      {/* X-axis labels */}
      <div style={{ 
        marginLeft: '100px', 
        marginRight: '70px', 
        height: '40px', // Increased from 20px to accommodate longer labels
        display: 'flex', 
        alignItems: 'flex-end', // Changed from 'center' to 'flex-end' to align at bottom
        justifyContent: 'space-between',
        fontSize: '10px', // Slightly increased from 9px for better readability
        color: '#6B7280',
        fontFamily: 'Inter, system-ui, sans-serif',
        paddingBottom: '8px' // Add some padding at the bottom
      }}>
        {sortedData.map((item, index) => {
          // Show all labels if there are 6 or fewer data points, otherwise show every 3rd label
          const shouldShowLabel = sortedData.length <= 6 || index % 3 === 0 || index === sortedData.length - 1;
          
          if (!shouldShowLabel) {
            return null;
          }
          
          return (
            <span key={`${item._id?.year}-${item._id?.month}-${index}`} style={{ 
              transform: 'rotate(-45deg)', 
              transformOrigin: 'bottom center', // Changed from 'center' to 'bottom center'
              minWidth: '20px', // Increased from 15px
              textAlign: 'center',
              whiteSpace: 'nowrap',
              overflow: 'visible', // Changed from 'hidden' to 'visible'
              fontSize: '10px',
              fontWeight: '500'
            }}>
              {formatMonthLabel(item)}
            </span>
          );
        })}
      </div>
    </div>
  );
};

// AgingAnalysisWidget Component - Matching Dashboard.tsx reference design
const AgingAnalysisWidget = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div style={{ height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#6B7280' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
          <p style={{ fontSize: '14px', fontFamily: 'Inter, system-ui, sans-serif' }}>Loading aging data...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#6B7280' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏰</div>
          <p style={{ fontSize: '14px', fontFamily: 'Inter, system-ui, sans-serif' }}>No aging data available</p>
        </div>
      </div>
    );
  }

  // Calculate dynamic values based on actual data
  const totalAmount = data?.summary?.totalAmount || 0;
  const totalRecords = data?.summary?.totalDenials || 0;
  const avgPerDenial = totalAmount && totalRecords ? totalAmount / totalRecords : 0;
  
  // Calculate overdue percentage dynamically
  const overduePercentage = totalAmount > 0 ? 100.0 : 0.0; // For now showing 100% as per design, but this should be calculated based on actual aging data

  return (
    <div style={{ height: '350px', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#111827',
          margin: 0,
          fontFamily: 'Inter, system-ui, sans-serif'
        }}>
          Aging Analysis
        </h3>
        <div style={{
          width: '20px',
          height: '20px',
          color: '#6B7280'
        }}>
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
        {/* Avg per Denial Card */}
        <div style={{
          flex: 1,
          backgroundColor: '#F0F9FF',
          borderRadius: '8px',
          padding: '12px',
          border: '1px solid #BAE6FD'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '16px',
              height: '16px',
              color: '#10B981'
            }}>
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>
                Avg per Denial
              </div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                {formatCurrency(avgPerDenial)}
              </div>
            </div>
          </div>
        </div>

        {/* Overdue Card */}
        <div style={{
          flex: 1,
          backgroundColor: '#FEF2F2',
          borderRadius: '8px',
          padding: '12px',
          border: '1px solid #FECACA'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '16px',
              height: '16px',
              color: '#EF4444'
            }}>
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>
                Overdue (60+ Days)
              </div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#EF4444' }}>
                {overduePercentage.toFixed(1)}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Bar */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '16px',
              height: '16px',
              backgroundColor: '#0E4C88',
              borderRadius: '2px'
            }} />
            <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>
              {formatNumber(totalRecords)} ({totalRecords > 0 ? '100.0' : '0.0'}%)
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '16px',
              height: '16px',
              backgroundColor: '#10B981',
              borderRadius: '2px'
            }} />
            <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>
              {formatCurrency(totalAmount)} ({totalAmount > 0 ? '100.0' : '0.0'}% of total)
            </span>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div style={{
          width: '100%',
          height: '8px',
          backgroundColor: '#F3F4F6',
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#DC2626',
            borderRadius: '4px'
          }} />
        </div>
      </div>

      {/* Key Insights */}
      <div style={{ flex: 1 }}>
        <h4 style={{
          fontSize: '14px',
          fontWeight: '600',
          color: '#111827',
          margin: '0 0 12px 0',
          fontFamily: 'Inter, system-ui, sans-serif'
        }}>
          Key Insights
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '4px',
              height: '4px',
              backgroundColor: '#10B981',
              borderRadius: '50%'
            }} />
            <span style={{ fontSize: '12px', color: '#6B7280', fontFamily: 'Inter, system-ui, sans-serif' }}>
              Current denials: 0.0% of total value
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '4px',
              height: '4px',
              backgroundColor: '#EF4444',
              borderRadius: '50%'
            }} />
            <span style={{ fontSize: '12px', color: '#6B7280', fontFamily: 'Inter, system-ui, sans-serif' }}>
              {overduePercentage.toFixed(1)}% of value is 60+ days overdue
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '4px',
              height: '4px',
              backgroundColor: '#3B82F6',
              borderRadius: '50%'
            }} />
            <span style={{ fontSize: '12px', color: '#6B7280', fontFamily: 'Inter, system-ui, sans-serif' }}>
              Average denial amount: {formatCurrency(avgPerDenial)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// DenialReasonsTreemap Component - Matching DenialReasonsAnalysis.tsx reference design
const DenialReasonsTreemap = ({ data, loading = false, height = 400 }) => {
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: `${height}px` }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: `${height}px` }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#6B7280', marginBottom: '8px', fontSize: '14px', fontFamily: 'Inter, system-ui, sans-serif' }}>
            No denial reasons data available
          </p>
          <p style={{ fontSize: '12px', color: '#9CA3AF', fontFamily: 'Inter, system-ui, sans-serif' }}>
            Try adjusting your filters or date range
          </p>
                        </div>
                      </div>
    );
  }

  // Sort data by totalAmount descending and take top 15
  const sortedData = [...data]
    .sort((a, b) => (b.totalAmount || 0) - (a.totalAmount || 0))
    .slice(0, 15);

  // Color palette matching the original (red to yellow gradient)
  const colors = [
    '#dc2626', '#dc2626', '#dc2626', // Top 3 - Dark red
    '#ef4444', '#ef4444', // 4-5 - Red  
    '#f97316', '#f97316', // 6-7 - Orange
    '#ea580c', '#ea580c', // 8-9 - Dark orange
    '#eab308', '#eab308', // 10-11 - Yellow
    '#ca8a04', '#ca8a04', '#a16207' // 12-14 - Darker yellow/brown
  ];

  return (
    <div style={{ width: '100%' }}>
      {/* Chart Title - Matching reference */}
      {/* <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#111827',
          margin: '0 0 6px 0',
          fontFamily: 'Inter, system-ui, sans-serif'
        }}>
          Denial Reasons by Financial Impact
        </h3>
        <p style={{
          fontSize: '14px',
          color: '#6B7280',
          margin: 0,
          fontFamily: 'Inter, system-ui, sans-serif'
        }}>
          Size represents total amount, color intensity shows impact level
        </p>
              </div> */}

      {/* Treemap Layout - Matching reference design */}
      <div className="treemap-container mt-4" style={{
        width: '100%',
        height: `${height - 80}px`,
        position: 'relative',
        backgroundColor: 'white',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {/* Row 1 - Top 2 largest items */}
        <div style={{ display: 'flex', height: '45%' }}>
          {/* Largest item (spans ~65% width) */}
          {sortedData[0] && (
            <div style={{
              flex: '0 0 65%',
              backgroundColor: colors[0],
              color: 'white',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              border: '2px solid white',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              const tooltip = document.createElement('div');
              tooltip.style.cssText = `
                position: fixed;
                background: rgba(0, 0, 0, 0.95);
                color: white;
                padding: 16px;
                border-radius: 8px;
                font-size: 13px;
                font-family: Inter, system-ui, sans-serif;
                pointer-events: none;
                z-index: 9999;
                max-width: 350px;
                white-space: normal;
                line-height: 1.4;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                border: 1px solid rgba(255, 255, 255, 0.1);
              `;
              tooltip.innerHTML = `
                <div style="font-weight: 600; margin-bottom: 8px; color: #FBBF24; font-size: 14px;">${sortedData[0]._id}</div>
                <div style="margin-bottom: 6px;">💰 Financial Impact: <strong>${formatCurrency(sortedData[0].totalAmount || 0)}</strong></div>
                <div style="margin-bottom: 6px;">📊 Denial Count: <strong>${formatNumber(sortedData[0].count || 0)}</strong></div>
                <div style="margin-bottom: 6px;">📈 Average Amount: <strong>${formatCurrency(sortedData[0].avgAmount || 0)}</strong></div>
                <div style="margin-bottom: 6px;">🏷️ Reason Code: <strong>${sortedData[0].reasonCode || 'N/A'}</strong></div>
                <div>📂 Category: <strong>${sortedData[0].category || 'N/A'}</strong></div>
              `;
              tooltip.id = 'treemap-tooltip';
              document.body.appendChild(tooltip);
              
              const rect = e.target.getBoundingClientRect();
              const tooltipWidth = 350;
              const tooltipHeight = 140;
              
              // Calculate initial position
              let tooltipX = rect.left + rect.width/2 - tooltipWidth/2;
              let tooltipY = rect.top - tooltipHeight - 15;
              
              // Adjust if tooltip would go off the left edge
              if (tooltipX < 10) {
                tooltipX = 10;
              }
              
              // Adjust if tooltip would go off the right edge
              if (tooltipX + tooltipWidth > window.innerWidth - 10) {
                tooltipX = window.innerWidth - tooltipWidth - 10;
              }
              
              // Adjust if tooltip would go off the top edge
              if (tooltipY < 10) {
                tooltipY = rect.bottom + 15;
              }
              
              // Adjust if tooltip would go off the bottom edge
              if (tooltipY + tooltipHeight > window.innerHeight - 10) {
                tooltipY = rect.top - tooltipHeight - 15;
              }
              
              tooltip.style.left = `${tooltipX}px`;
              tooltip.style.top = `${tooltipY}px`;
            }}
            onMouseLeave={() => {
              const tooltip = document.getElementById('treemap-tooltip');
              if (tooltip) tooltip.remove();
            }}
            title={`${sortedData[0]._id}\nImpact: ${formatCurrency(sortedData[0].totalAmount || 0)}\nCount: ${formatNumber(sortedData[0].count || 0)}`}
            >
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', lineHeight: '1.3', wordBreak: 'break-word' }}>
                {sortedData[0]._id.length > 45 ? `${sortedData[0]._id.substring(0, 45)}...` : sortedData[0]._id}
          </div>
              <div style={{ fontSize: '18px', fontWeight: '700' }}>
                {formatCurrency(sortedData[0].totalAmount || 0)}
        </div>
            </div>
          )}
          
          {/* Second largest (spans ~35% width) */}
          {sortedData[1] && (
            <div style={{
              flex: '0 0 35%',
              backgroundColor: colors[1],
              color: 'white',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              border: '2px solid white',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              const tooltip = document.createElement('div');
              tooltip.style.cssText = `
                position: fixed;
                background: rgba(0, 0, 0, 0.95);
                color: white;
                padding: 16px;
                border-radius: 8px;
                font-size: 13px;
                font-family: Inter, system-ui, sans-serif;
                pointer-events: none;
                z-index: 9999;
                max-width: 300px;
                white-space: normal;
                line-height: 1.4;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                border: 1px solid rgba(255, 255, 255, 0.1);
              `;
              tooltip.innerHTML = `
                <div style="font-weight: 600; margin-bottom: 8px; color: #FBBF24; font-size: 14px;">${sortedData[1]._id}</div>
                <div style="margin-bottom: 6px;">💰 Financial Impact: <strong>${formatCurrency(sortedData[1].totalAmount || 0)}</strong></div>
                <div style="margin-bottom: 6px;">📊 Denial Count: <strong>${formatNumber(sortedData[1].count || 0)}</strong></div>
                <div style="margin-bottom: 6px;">📈 Average Amount: <strong>${formatCurrency(sortedData[1].avgAmount || 0)}</strong></div>
                <div style="margin-bottom: 6px;">🏷️ Reason Code: <strong>${sortedData[1].reasonCode || 'N/A'}</strong></div>
                <div>📂 Category: <strong>${sortedData[1].category || 'N/A'}</strong></div>
              `;
              tooltip.id = 'treemap-tooltip';
              document.body.appendChild(tooltip);
              
              const rect = e.target.getBoundingClientRect();
              const tooltipWidth = 300;
              const tooltipHeight = 140;
              
              // Calculate initial position
              let tooltipX = rect.left + rect.width/2 - tooltipWidth/2;
              let tooltipY = rect.top - tooltipHeight - 15;
              
              // Adjust if tooltip would go off the left edge
              if (tooltipX < 10) {
                tooltipX = 10;
              }
              
              // Adjust if tooltip would go off the right edge
              if (tooltipX + tooltipWidth > window.innerWidth - 10) {
                tooltipX = window.innerWidth - tooltipWidth - 10;
              }
              
              // Adjust if tooltip would go off the top edge
              if (tooltipY < 10) {
                tooltipY = rect.bottom + 15;
              }
              
              // Adjust if tooltip would go off the bottom edge
              if (tooltipY + tooltipHeight > window.innerHeight - 10) {
                tooltipY = rect.top - tooltipHeight - 15;
              }
              
              tooltip.style.left = `${tooltipX}px`;
              tooltip.style.top = `${tooltipY}px`;
            }}
            onMouseLeave={() => {
              const tooltip = document.getElementById('treemap-tooltip');
              if (tooltip) tooltip.remove();
            }}
            title={`${sortedData[1]._id}\nImpact: ${formatCurrency(sortedData[1].totalAmount || 0)}\nCount: ${formatNumber(sortedData[1].count || 0)}`}
            >
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', lineHeight: '1.3', wordBreak: 'break-word' }}>
                {sortedData[1]._id.length > 30 ? `${sortedData[1]._id.substring(0, 30)}...` : sortedData[1]._id}
          </div>
              <div style={{ fontSize: '16px', fontWeight: '700' }}>
                {formatCurrency(sortedData[1].totalAmount || 0)}
                </div>
            </div>
          )}
      </div>

        {/* Row 2 - Next level items */}
        <div style={{ display: 'flex', height: '30%' }}>
          {/* Third item */}
          {sortedData[2] && (
            <div style={{
              flex: '0 0 50%',
              backgroundColor: colors[2],
              color: 'white',
              padding: '14px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              border: '2px solid white',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.01)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            title={`${sortedData[2]._id}\nImpact: ${formatCurrency(sortedData[2].totalAmount || 0)}`}
            >
              <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '6px', lineHeight: '1.3', wordBreak: 'break-word' }}>
                {sortedData[2]._id.length > 25 ? `${sortedData[2]._id.substring(0, 25)}...` : sortedData[2]._id}
        </div>
              <div style={{ fontSize: '14px', fontWeight: '700' }}>
                {formatCurrency(sortedData[2].totalAmount || 0)}
              </div>
            </div>
          )}

          {/* Right column for items 4-6 */}
          <div style={{ flex: '0 0 50%', display: 'flex', flexDirection: 'column' }}>
            {sortedData.slice(3, 6).map((item, index) => (
              <div key={item._id || index} style={{
                flex: 1,
                backgroundColor: colors[index + 3],
                color: 'white',
                padding: '10px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                border: '2px solid white',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.01)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              title={`${item._id}\nImpact: ${formatCurrency(item.totalAmount || 0)}`}
              >
                <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '4px', lineHeight: '1.2', wordBreak: 'break-word' }}>
                  {item._id.length > 18 ? `${item._id.substring(0, 18)}...` : item._id}
                </div>
                <div style={{ fontSize: '12px', fontWeight: '700' }}>
                  {formatCurrency(item.totalAmount || 0)}
              </div>
            </div>
            ))}
          </div>
      </div>

        {/* Row 3 - Smaller items */}
        <div style={{ display: 'flex', height: '25%' }}>
          {sortedData.slice(6, 12).map((item, index) => (
            <div key={item._id || index} style={{
              flex: 1,
              backgroundColor: colors[index + 6],
              color: 'white',
              padding: '8px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              border: '2px solid white',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            title={`${item._id}\nImpact: ${formatCurrency(item.totalAmount || 0)}`}
            >
              <div style={{ fontSize: '10px', fontWeight: '600', marginBottom: '3px', lineHeight: '1.2', wordBreak: 'break-word' }}>
                {item._id.length > 12 ? `${item._id.substring(0, 12)}...` : item._id}
          </div>
              <div style={{ fontSize: '11px', fontWeight: '700' }}>
                {formatCurrency(item.totalAmount || 0)}
            </div>
            </div>
          ))}
          </div>
        </div>

      {/* Help Text - Matching reference */}
      <div style={{
        marginTop: '16px',
        padding: '12px',
        backgroundColor: '#E7EEF4',
        borderRadius: '8px',
        border: '1px solid #E7EEF4'
      }}>
        <p style={{
          fontSize: '12px',
          color: '#0E4C88',
          margin: 0,
          lineHeight: '1.4',
          fontFamily: 'Inter, system-ui, sans-serif'
        }}>
          <strong>💡 How to read this chart:</strong> Each rectangle represents a denial reason. 
          The size shows the total financial impact, while the color intensity indicates the severity level. 
          Click on any rectangle to see detailed information about that denial reason.
        </p>
          </div>
              </div>
  );
};

// Login Detection Hook
const useLoginDetection = () => {
  const [isLogin, setIsLogin] = useState(false);

  useEffect(() => {
    const loginTimestamp = localStorage.getItem('loginTimestamp');
    const currentTime = Date.now();
    const isRecentLogin = loginTimestamp && (currentTime - parseInt(loginTimestamp)) < 30000; // 30 seconds
    
    if (isRecentLogin) {
      setIsLogin(true);
      localStorage.removeItem('loginTimestamp'); // Clear after use
    } else {
      setIsLogin(false);
    }
  }, []);

  return { isLogin };
};

// Navigation Detection Hook
const useNavigationDetection = () => {
  const [isNavigation, setIsNavigation] = useState(false);

  useEffect(() => {
    // Check if this is a page refresh or navigation
    const navigationEntry = performance.getEntriesByType('navigation')[0];
    const isPageRefresh = navigationEntry?.type === 'reload';
    const isNavigation = navigationEntry?.type === 'navigate';
    
    console.log('🔍 Navigation Detection:', {
      navigationType: navigationEntry?.type,
      isPageRefresh,
      isNavigation
    });
    
    if (isPageRefresh || isNavigation) {
      console.log('🚀 Setting isNavigation to true');
      setIsNavigation(true);
      // Clear after a longer delay to allow HexagonalLoader to be visible
      const timer = setTimeout(() => {
        console.log('⏰ Clearing isNavigation');
        setIsNavigation(false);
      }, 3500); // 3.5 seconds to match HexagonalLoader timing
      return () => clearTimeout(timer);
    }
  }, []);

  return { isNavigation };
};

// New Hexagonal Loader Component
const HexagonalLoader = () => {
  return (
    <NanoLoaderWrapper>
      <NanoProgressContainer>
        <NanoLottieCard>
          <NanoXVSLottie
            className="loading-files"
            animationData={LoadingFiles}
            loop={true}
          />
          <NanoConnectionText>
            <NanoRadioButton />
           Fetching Data...
          </NanoConnectionText>
        </NanoLottieCard>
      </NanoProgressContainer>
    </NanoLoaderWrapper>
  );
};

const NanoDashboard = () => {
  const [dateRange, setDateRange] = useState({}); // Currently "All time" - can be expanded when date picker is implemented
  const [filters, setFilters] = useState({
    provider: '',
    carrier: '',
    category: ''
  });
  const [showLottieLoader, setShowLottieLoader] = useState(false);
  const [showHexagonalLoader, setShowHexagonalLoader] = useState(false);
  const [isRefreshButtonClicked, setIsRefreshButtonClicked] = useState(false);
  const { isLogin } = useLoginDetection();
  const { isNavigation } = useNavigationDetection();

  const { data: analytics, isLoading: analyticsLoading, error: analyticsError, refetch: refetchAnalytics } = useDenialReasonsAnalytics({
    ...dateRange,
    ...filters
  });

  const { data: trends, isLoading: trendsLoading, refetch: refetchTrends } = useDashboardTrends({
    ...dateRange,
    ...filters
  });

  // Pass only upstream filters to get all available options for each dropdown
  // For provider dropdown: no upstream filters
  // For carrier dropdown: only provider filter
  // For category dropdown: provider and carrier filters
  const { data: filterOptions } = useAnalyticsFilterOptions({
    provider: filters.provider
  });

  // Control Lottie loader timing
  useEffect(() => {
    if (isLogin) {
      setShowLottieLoader(true);
      const timer = setTimeout(() => {
        setShowLottieLoader(false);
      }, 9000); // 9 seconds
      return () => clearTimeout(timer);
    }
  }, [isLogin]);

  // Control HexagonalLoader timing - only show on navigation, refresh, or refresh button
  useEffect(() => {
    console.log('🔧 HexagonalLoader useEffect:', { isNavigation, isRefreshButtonClicked });
    if (isNavigation || isRefreshButtonClicked) {
      console.log('🎯 Showing HexagonalLoader');
      setShowHexagonalLoader(true);
      const timer = setTimeout(() => {
        console.log('⏰ Hiding HexagonalLoader');
        setShowHexagonalLoader(false);
        setIsRefreshButtonClicked(false);
      }, 2000); // 2 seconds
      return () => clearTimeout(timer);
    }
  }, [isNavigation, isRefreshButtonClicked]);

  

  const handleFilterChange = (key, value) => {
    console.log(`🔧 Filter ${key}:`, value || 'All'); // Simplified debug log
    
    // Reset dependent filters when parent filter changes
    let newFilters = { ...filters, [key]: value };
    
    // If provider changes, reset carrier and category
    if (key === 'provider') {
      newFilters = { ...newFilters, carrier: '', category: '' };
    }
    // If carrier changes, reset category
    else if (key === 'carrier') {
      newFilters = { ...newFilters, category: '' };
    }
    
    setFilters(newFilters);
  };

  const handleDateRangeChange = (startDate, endDate) => {
    console.log('📅 Date range:', startDate && endDate ? `${startDate} to ${endDate}` : 'All time');
    setDateRange({ startDate, endDate });
  };

  const handleRefresh = () => {
    console.log('🔄 Refreshing all data...');
    // Set refresh button clicked flag to show hexagonal loader
    setIsRefreshButtonClicked(true);
    
    // Reset all filters to trigger fresh data fetch
    setFilters({
      provider: '',
      carrier: '',
      category: ''
    });
    setDateRange({});
    
    // Force refetch all data
    refetchAnalytics();
    refetchTrends();
  };

  const handleExport = async () => {
    try {
      console.log('📊 Exporting data...');
      const params = new URLSearchParams();
      
      // Add all current filters to export
      if (filters.provider) params.append('provider', filters.provider);
      if (filters.carrier) params.append('carrier', filters.carrier);
      if (filters.category) params.append('category', filters.category);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      
      const response = await axios.get(`${config.api.base_url}/nano-denial/analytics/export-denial-reasons?${params}`, {
        headers: {
          'Authorization': `Bearer ${getUserToken()}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `denial-reasons-export-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Export completed successfully');
    } catch (error) {
      console.error('❌ Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  useEffect(() => {
    return () => {
      // Cleanup tooltips on unmount
      const chartTooltip = document.getElementById('chart-tooltip');
      const treemapTooltip = document.getElementById('treemap-tooltip');
      if (chartTooltip) chartTooltip.remove();
      if (treemapTooltip) treemapTooltip.remove();
    };
  }, []);

  if (analyticsError) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <WarningOutlined style={{ fontSize: '48px', color: '#EF4444', marginBottom: '16px' }} />
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
            Unable to load dashboard data
          </h2>
          <p style={{ color: '#6B7280' }}>Please check your connection and try again.</p>
                      </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <Col lg={24} xl={24} xs={0} md={0}>
        <Header
          subHeader="Denial Reasons Analysis"
          headerTag="Deep dive into denial patterns and root causes"
        />
      </Col>
      <Col lg={0} xl={0} xs={24} md={24}>
        <HeaderFile activeNav="dashboard" />
      </Col>
      {isLogin && showLottieLoader ? (
        <NanoLoaderWrapper>
          <NanoXVSLottie
            className="dashboard-loader"
            animationData={NanoLoaderJson}
            loop={false}
          />
        </NanoLoaderWrapper>
      ) : ((isNavigation || isRefreshButtonClicked) && showHexagonalLoader) ? (
        <HexagonalLoader  />
      ) : (
       
      <div style={{ 
        padding: '24px',
        paddingTop: '0px',
        fontFamily: 'Inter, system-ui, sans-serif'
      }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '24px'
        }}>
          {/* Page Header - EXACT replica from DenialReasonsAnalysis.tsx */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '8px',
            paddingTop: '0px'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: window.innerWidth < 1024 ? 'column' : 'row',
              alignItems: window.innerWidth < 1024 ? 'flex-start' : 'center',
              justifyContent: 'space-between',
              gap: '8px'
            }}>
                        <div>
                <h1 style={{ 
                  fontSize: '24px', 
                  fontWeight: '700', 
                  color: '#111827',
                  margin: 0,
                  paddingTop: '0px',
                  fontFamily: 'Inter, system-ui, sans-serif'
                }}>
                  Denial Reasons Analysis
                </h1>
                <p style={{ 
                  fontSize: '16px', 
                  color: '#6B7280', 
                  margin: 0,
                  marginTop: '4px',
                  fontFamily: 'Inter, system-ui, sans-serif'
                }}>
                  Deep dive into denial patterns and root causes
                </p>
                {/* <p style={{ 
                  fontSize: '14px', 
                  color: '#000000', 
                  margin: 0,
                  marginTop: '4px',
                  fontFamily: 'Inter, system-ui, sans-serif'
                }}>
                  Analyzing <span style={{ color: '#0E4C88' }}>{analytics?.summary?.totalDenials || 0}</span> records with denial reasons out of <span style={{ color: '#0E4C88' }}>{analytics?.summary?.totalDenials || 0}</span> total records
                </p> */}
                        </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px'
              }}>
                {/* Date Range Filter */}
                <DateRangeFilter
                  startDate={dateRange?.startDate}
                  endDate={dateRange?.endDate}
                  onDateChange={handleDateRangeChange}
                />
                
                {/* Refresh button */}
          <button
            onClick={handleRefresh}
                  disabled={analyticsLoading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    backgroundColor: '#0E4C88',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '400',
                    cursor: analyticsLoading ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    opacity: analyticsLoading ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!analyticsLoading) {
                      e.target.style.backgroundColor = '#0c3d6b';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!analyticsLoading) {
                      e.target.style.backgroundColor = '#0E4C88';
                    }
                  }}
                >
                  <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <polyline points="23,4 23,10 17,10"></polyline>
                    <polyline points="1,20 1,14 7,14"></polyline>
                    <path d="m3.51,9a9,9,0,0,1,14.85-3.36L23,10M1,14l4.64,4.36A9,9,0,0,0,20.49,15"></path>
                  </svg>
                  <span>Refresh</span>
                </button>

                {/* Export Excel button */}
                <button
                  onClick={handleExport}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    color: 'rgb(14, 76, 136)',
                    backgroundColor: 'rgb(255, 255, 255)',
                    border: '1px solid rgb(14, 76, 136)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '400',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    fontFamily: 'Inter, system-ui, sans-serif'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'rgb(255, 255, 255)';
                  }}
                >
                  <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="m3 16 4 4 4-4"></path>
                    <path d="M7 20V4"></path>
                    <path d="m15 8 4-4 4 4"></path>
                    <path d="M19 4v16"></path>
                  </svg>
                  <span>Export Excel</span>
          </button>
                
                <div style={{ 
                  fontSize: '14px', 
                  color: '#6B7280',
                  fontFamily: 'Inter, system-ui, sans-serif'
                }}>
                  {/* Last updated: {analytics?.lastUpdated ? new Date(analytics.lastUpdated).toLocaleTimeString() : '7:07:43 PM'} */}
                    </div>
                  </div>
                        </div>
                      </div>
                      
          {/* Filter Bar */}
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            border: '1px solid #E5E7EB'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <FilterOutlined style={{ fontSize: '20px', color: '#6B7280' }} />
              <div style={{ 
                flex: 1, 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '16px' 
              }}>
                                  <select
                    value={filters.provider}
                    onChange={(e) => handleFilterChange('provider', e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      color: '#374151',
                      outline: 'none',
                      boxShadow: 'none',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#0E4C88';
                      e.target.style.boxShadow = '0 0 0 2px rgba(14, 76, 136, 0.2)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#D1D5DB';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="">All Providers</option>
                    {filterOptions?.providers?.map((provider) => (
                      <option key={provider} value={provider}>{provider}</option>
                    ))}
                  </select>
                
                <select
                  value={filters.carrier}
                  onChange={(e) => handleFilterChange('carrier', e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#374151',
                    outline: 'none',
                    boxShadow: 'none',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0E4C88';
                    e.target.style.boxShadow = '0 0 0 2px rgba(14, 76, 136, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#D1D5DB';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">All Carriers</option>
                  {filterOptions?.carriers?.map((carrier) => (
                    <option key={carrier} value={carrier}>{carrier}</option>
                  ))}
                </select>
                
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#374151',
                    outline: 'none',
                    boxShadow: 'none',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0E4C88';
                    e.target.style.boxShadow = '0 0 0 2px rgba(14, 76, 136, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#D1D5DB';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">All Categories</option>
                  {filterOptions?.categories?.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                        </div>
                        </div>
                      </div>
                      
          {/* Key Metrics - EXACT replica from DenialReasonsAnalysis.tsx */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '24px'
          }}>
        <StatCard
              title="Total Denial Reasons"
              value={analytics?.summary?.totalReasons || 0}
              format="number"
              icon={BarChartOutlined}
              loading={analyticsLoading}
        />
        
        <StatCard
          title="Total Denials"
              value={analytics?.summary?.totalDenials || 0}
          format="number"
              icon={WarningOutlined}
              loading={analyticsLoading}
              trend={analytics?.trends?.denialsTrend}
        />
        
        <StatCard
              title="Financial Impact"
              value={analytics?.summary?.totalAmount || 0}
          format="currency"
              icon={DollarOutlined}
              loading={analyticsLoading}
              trend={analytics?.trends?.amountTrend}
        />
        
        <StatCard
              title="Avg per Denial"
              value={analytics?.summary?.totalAmount && analytics?.summary?.totalDenials 
                ? analytics.summary.totalAmount / analytics.summary.totalDenials 
                : 0}
          format="currency"
              icon={RiseOutlined}
              loading={analyticsLoading}
        />
                        </div>

          {/* Charts Row - Denial Trends and Aging Analysis */}
          <div className="charts-row" style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth < 1024 ? '1fr' : '3fr 1fr',
            gap: '24px',
            marginTop: '24px'
          }}>
            {/* Denial Trends Over Time Chart */}
            <div className="chart-container" style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 6px -1px rgba(14, 76, 136, 0.1), 0 2px 4px -1px rgba(14, 76, 136, 0.06)',
              border: '1px solid #E5E7EB',
              padding: '24px',
              minWidth: window.innerWidth < 768 ? 'auto' : '600px'
            }}>
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#111827',
                  margin: 0,
                  fontFamily: 'Inter, system-ui, sans-serif'
                }}>
                  Denial Trends Over Time
                </h3>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                  marginTop: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '12px',
                      height: '3px',
                      backgroundColor: '#0E4C88',
                      borderRadius: '2px'
                    }}></div>
                    <span style={{ fontSize: '14px', color: '#6B7280', fontFamily: 'Inter, system-ui, sans-serif' }}>Total Amount</span>
                        </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: '#EF4444',
                      borderRadius: '2px'
                    }}></div>
                    <span style={{ fontSize: '14px', color: '#6B7280', fontFamily: 'Inter, system-ui, sans-serif' }}>Denial Count</span>
                      </div>
              </div>
              </div>
              
              {trendsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                  <Spin size="large" />
                </div>
              ) : (
                <TrendsChart data={trends?.monthlyTrends || []} />
            )}
          </div>

            {/* Aging Analysis Widget */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 6px -1px rgba(14, 76, 136, 0.1), 0 2px 4px -1px rgba(14, 76, 136, 0.06)',
              border: '1px solid #E5E7EB',
              padding: '24px',
              flex: '1',
              minWidth: '400px'
            }}>
              <AgingAnalysisWidget data={analytics} loading={analyticsLoading} />
            </div>
        </div>

          {/* Denial Reasons Treemap Section */}
          <div style={{ marginTop: '24px' }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 6px -1px rgba(14, 76, 136, 0.1), 0 2px 4px -1px rgba(14, 76, 136, 0.06)',
              border: '1px solid #E5E7EB',
              padding: '24px'
            }}>
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#111827',
                  margin: 0,
                  fontFamily: 'Inter, system-ui, sans-serif'
                }}>
                  Denial Reasons by Volume & Impact
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  margin: '4px 0 0 0',
                  fontFamily: 'Inter, system-ui, sans-serif'
                }}>
                  Interactive Treemap showing relative size and financial impact of denial reasons
                </p>
          </div>

              <DenialReasonsTreemap
                data={analytics?.denialReasons || []}
                loading={analyticsLoading}
                height={450}
              />
                </div>
              </div>
              
          {/* Detailed Tables Section */}
          <div style={{ marginTop: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '50% 50%', gap: '24px', paddingRight: '24px' }}>
              {/* Top Denial Reasons Table */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(14, 76, 136, 0.1), 0 2px 4px -1px rgba(14, 76, 136, 0.06)',
                border: '1px solid #E5E7EB',
                padding: '24px',
                overflow: 'hidden'
              }}>
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: 0,
                    fontFamily: 'Inter, system-ui, sans-serif'
                  }}>
                    Top Denial Reasons
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    margin: '4px 0 0 0',
                    fontFamily: 'Inter, system-ui, sans-serif'
                  }}>
                    Most frequent denial reasons ranked by count and financial impact
                  </p>
                </div>

                {analyticsLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                    <Spin size="large" />
                  </div>
                ) : (
                  <div style={{ 
                    overflowX: 'auto', 
                    width: '100%',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }} className="scrollable-table-container">
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                          <th style={{ 
                            padding: '12px 16px', 
                            textAlign: 'left', 
                            fontSize: '12px', 
                            fontWeight: '600', 
                            color: '#6B7280',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            whiteSpace: 'nowrap'
                          }}>
                            RANK
                          </th>
                          <th style={{ 
                            padding: '12px 16px', 
                            textAlign: 'left', 
                            fontSize: '12px', 
                            fontWeight: '600', 
                            color: '#6B7280',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            whiteSpace: 'nowrap'
                          }}>
                            DENIAL REASON
                          </th>
                          <th style={{ 
                            padding: '12px 16px', 
                            textAlign: 'right', 
                            fontSize: '12px', 
                            fontWeight: '600', 
                            color: '#6B7280',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            whiteSpace: 'nowrap'
                          }}>
                            COUNT
                          </th>
                          <th style={{ 
                            padding: '12px 16px', 
                            textAlign: 'right', 
                            fontSize: '12px', 
                            fontWeight: '600', 
                            color: '#6B7280',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            whiteSpace: 'nowrap'
                          }}>
                            % OF TOTAL
                          </th>
                          <th style={{ 
                            padding: '12px 16px', 
                            textAlign: 'right', 
                            fontSize: '12px', 
                            fontWeight: '600', 
                            color: '#6B7280',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            whiteSpace: 'nowrap'
                          }}>
                            AMOUNT
                          </th>
                          <th style={{ 
                            padding: '12px 16px', 
                            textAlign: 'right', 
                            fontSize: '12px', 
                            fontWeight: '600', 
                            color: '#6B7280',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            whiteSpace: 'nowrap'
                          }}>
                            AVG AM
                          </th>
                          <th style={{ 
                            padding: '12px 16px', 
                            textAlign: 'left', 
                            fontSize: '12px', 
                            fontWeight: '600', 
                            color: '#6B7280',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            whiteSpace: 'nowrap'
                          }}>
                            CATEGORY
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(analytics?.denialReasons || []).sort((a, b) => (b.count || 0) - (a.count || 0)).map((item, index) => {
                          const percentage = analytics?.summary?.totalDenials 
                            ? ((item.count || 0) / analytics.summary.totalDenials * 100).toFixed(1)
                            : '0.0';
                          const avgAmount = item.count ? (item.totalAmount || 0) / item.count : 0;
                          
                          return (
                            <tr key={item._id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                              <td style={{ 
                                padding: '12px 16px', 
                                fontSize: '14px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                whiteSpace: 'nowrap'
                              }}>
                                <div style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '24px',
                                  height: '24px',
                                  backgroundColor: '#0E4C88',
                                  color: 'white',
                                  borderRadius: '50%',
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}>
                                  #{index + 1}
                                </div>
                              </td>
                              <td style={{ 
                                padding: '12px 16px', 
                                fontSize: '14px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                maxWidth: '400px'
                              }}>
                                <div>
                                  <div style={{ 
                                    fontWeight: '500', 
                                    color: '#111827', 
                                    marginBottom: '2px',
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word'
                                  }}>
                                    {item._id}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                    Code: {item.reasonCode || 'N/A'}
                                  </div>
                                </div>
                              </td>
                              <td style={{ 
                                padding: '12px 16px', 
                                textAlign: 'right',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#111827',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                whiteSpace: 'nowrap'
                              }}>
                                {formatNumber(item.count || 0)}
                              </td>
                              <td style={{ 
                                padding: '12px 16px', 
                                textAlign: 'right',
                                fontSize: '14px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                whiteSpace: 'nowrap'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                                  <span style={{ color: '#111827' }}>{percentage}%</span>
                                  <div style={{
                                    width: '6px',
                                    height: '6px',
                                    backgroundColor: index < 3 ? '#EF4444' : '#9CA3AF',
                                    borderRadius: '50%'
                                  }}></div>
                                </div>
                              </td>
                              <td style={{ 
                                padding: '12px 16px', 
                                textAlign: 'right',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#111827',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                whiteSpace: 'nowrap'
                              }}>
                                {formatCurrency(item.totalAmount || 0)}
                              </td>
                              <td style={{ 
                                padding: '12px 16px', 
                                textAlign: 'right',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#111827',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                whiteSpace: 'nowrap'
                              }}>
                                {formatCurrency(avgAmount)}
                              </td>
                              <td style={{ 
                                padding: '12px 16px', 
                                fontSize: '14px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                whiteSpace: 'nowrap'
                              }}>
                                <span style={{
                                  display: 'inline-flex',
                                  padding: '4px 8px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  borderRadius: '12px',
                                  backgroundColor: item.category?.includes('Patient') ? '#DBEAFE' :
                                    item.category?.includes('Noncovered') ? '#FEE2E2' :
                                    item.category?.includes('Authorization') ? '#FEF3C7' :
                                    '#F3F4F6',
                                  color: item.category?.includes('Patient') ? '#1E40AF' :
                                    item.category?.includes('Noncovered') ? '#DC2626' :
                                    item.category?.includes('Authorization') ? '#D97706' :
                                    '#6B7280'
                                }}>
                                  {item.category || 'Uncategorized'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
      </div>

              {/* Financial Impact by Reason */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(14, 76, 136, 0.1), 0 2px 4px -1px rgba(14, 76, 136, 0.06)',
                border: '1px solid #E5E7EB',
                padding: '24px',
                overflow: 'hidden'
              }}>
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: 0,
                    fontFamily: 'Inter, system-ui, sans-serif'
                  }}>
                    Financial Impact by Reason
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    margin: '4px 0 0 0',
                    fontFamily: 'Inter, system-ui, sans-serif'
                  }}>
                    Revenue impact and collection metrics
                  </p>
          </div>

                {analyticsLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                    <Spin size="large" />
                  </div>
                ) : (
                  <div style={{ 
                    overflowX: 'auto', 
                    width: '100%',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }} className="scrollable-container">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
                      {(analytics?.denialReasons || []).sort((a, b) => (b.totalAmount || 0) - (a.totalAmount || 0)).map((item, index) => (
                        <div key={item._id} style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          padding: '16px',
                          backgroundColor: '#F9FAFB',
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB',
                          width: '100%',
                          gap: '16px',
                          minHeight: 'fit-content'
                        }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1, minWidth: 0 }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            backgroundColor: '#E7EEF4',
                            color: '#0E4C88',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            flexShrink: 0
                          }}>
                            #{index + 1}
                          </div>
                          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <div style={{
                              fontSize: '14px',
                              fontWeight: '500',
                              color: '#111827',
                              marginBottom: '6px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              lineHeight: '1.4',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                              whiteSpace: 'normal',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {item._id}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#6B7280',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              lineHeight: '1.3'
                            }}>
                              {formatNumber(item.count || 0)} denials • Avg {item.avgDaysOutstanding?.toFixed(0) || 0} days outstanding
                            </div>
                          </div>
                        </div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '700',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                          flexShrink: 0,
                          textAlign: 'right',
                          minWidth: '80px',
                          maxWidth: '120px',
                          alignSelf: 'flex-start',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {formatCurrency(item.totalAmount || 0)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                 )}
            </div>
          </div>
        </div>
      </div>

      {/* Actionable Insights & Recommendations */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px -1px rgba(14, 76, 136, 0.1), 0 2px 4px -1px rgba(14, 76, 136, 0.06)',
        border: '1px solid #E5E7EB',
        padding: '24px',
        marginTop: '24px'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            margin: 0,
            fontFamily: 'Inter, system-ui, sans-serif'
          }}>
            🎯 Actionable Insights & Recommendations
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#6B7280',
            margin: '4px 0 0 0',
            fontFamily: 'Inter, system-ui, sans-serif'
          }}>
            Based on your denial patterns analysis
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{
              padding: '16px',
              backgroundColor: '#FEF2F2',
              borderRadius: '8px',
              borderLeft: '4px solid #F87171'
            }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#991B1B',
                margin: '0 0 8px 0',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}>
                🚨 High Priority Actions
              </h4>
              <ul style={{
                fontSize: '14px',
                color: '#B91C1C',
                margin: 0,
                padding: 0,
                listStyle: 'none',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}>
                <li style={{ marginBottom: '4px' }}>• Address Sequestration denials ({analytics?.denialReasons?.[0]?.count || 186} cases, federal payment reduction)</li>
                <li style={{ marginBottom: '4px' }}>• Review fee schedule compliance for charge excess denials</li>
                <li style={{ marginBottom: '4px' }}>• Improve prior authorization workflows (26 denials)</li>
              </ul>
            </div>
            
            <div style={{
              padding: '16px',
              backgroundColor: '#FFFBEB',
              borderRadius: '8px',
              borderLeft: '4px solid #F59E0B'
            }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#92400E',
                margin: '0 0 8px 0',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}>
                ⚠️ Medium Priority
              </h4>
              <ul style={{
                fontSize: '14px',
                color: '#B45309',
                margin: 0,
                padding: 0,
                listStyle: 'none',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}>
                <li style={{ marginBottom: '4px' }}>• Enhance eligibility verification processes</li>
                <li style={{ marginBottom: '4px' }}>• Audit provider credentialing status</li>
                <li style={{ marginBottom: '4px' }}>• Review bundling rules and documentation</li>
              </ul>
            </div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{
              padding: '16px',
              backgroundColor: '#EFF6FF',
              borderRadius: '8px',
              borderLeft: '4px solid #60A5FA'
            }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#1E40AF',
                margin: '0 0 8px 0',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}>
                💡 Process Improvements
              </h4>
              <ul style={{
                fontSize: '14px',
                color: '#1D4ED8',
                margin: 0,
                padding: 0,
                listStyle: 'none',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}>
                <li style={{ marginBottom: '4px' }}>• Implement real-time eligibility verification</li>
                <li style={{ marginBottom: '4px' }}>• Create denial prevention training programs</li>
                <li style={{ marginBottom: '4px' }}>• Establish carrier-specific submission workflows</li>
              </ul>
            </div>
            
            <div style={{
              padding: '16px',
              backgroundColor: '#F0FDF4',
              borderRadius: '8px',
              borderLeft: '4px solid #34D399'
            }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#166534',
                margin: '0 0 8px 0',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}>
                📈 Revenue Opportunities
              </h4>
              <ul style={{
                fontSize: '14px',
                color: '#15803D',
                margin: 0,
                padding: 0,
                listStyle: 'none',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}>
                <li style={{ marginBottom: '4px' }}>• Focus appeals on high-value denied claims</li>
                <li style={{ marginBottom: '4px' }}>• Negotiate better terms with top denying carriers</li>
                <li style={{ marginBottom: '4px' }}>• Automate routine denial prevention checks</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
      )}
      <style>{`
        /* Import Inter font */
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        /* Base styles */
        * {
          font-family: 'Inter', system-ui, sans-serif;
        }
        
        /* Card styles - EXACT replica from original CSS */
        .stat-card {
          background-color: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px -1px rgba(14, 76, 136, 0.1), 0 2px 4px -1px rgba(14, 76, 136, 0.06);
          border: 1px solid #E5E7EB;
          padding: 16px;
          transition: box-shadow 0.2s ease;
        }
        
        .stat-card:hover {
          box-shadow: 0 10px 15px -3px rgba(14, 76, 136, 0.1), 0 4px 6px -2px rgba(14, 76, 136, 0.05);
        }
        
        /* Fix dropdown arrows */
        select {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right 8px center;
          background-size: 16px;
          padding-right: 32px !important;
        }
        
        /* Animation keyframes */
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Responsive grid adjustments */
        @media (max-width: 1024px) {
          .charts-row {
            grid-template-columns: 1fr !important;
          }
        }
        
        @media (max-width: 768px) {
          .stat-card {
            padding: 20px;
          }
          
          .charts-row {
            gap: 16px !important;
          }
        }
        
        @media (max-width: 640px) {
          .stat-card {
            padding: 16px;
          }
          
          .charts-row {
            gap: 12px !important;
          }
        }
        
        /* Chart specific responsive adjustments */
        @media (max-width: 1200px) {
          .charts-row {
            grid-template-columns: 2fr 1fr !important;
          }
        }
        
        @media (max-width: 768px) {
          .charts-row {
            grid-template-columns: 1fr !important;
          }
        }
        
        /* Scrollable table styles */
        .scrollable-table-container {
          position: relative;
        }
        
        .scrollable-table-container thead {
          position: sticky;
          top: 0;
          z-index: 10;
          background-color: #F9FAFB;
        }
        
        .scrollable-table-container thead th {
          background-color: #F9FAFB;
        }
        
        /* Scroll indicator styles */
        .scrollable-container {
          position: relative;
        }
        
        .scrollable-container::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 20px;
          background: linear-gradient(transparent, rgba(255, 255, 255, 0.8));
          pointer-events: none;
          z-index: 5;
        }
        
        /* Slim scroll styles */
        .scrollable-table-container::-webkit-scrollbar,
        .scrollable-container::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        
        .scrollable-table-container::-webkit-scrollbar-track,
        .scrollable-container::-webkit-scrollbar-track {
          background: transparent;
          border-radius: 3px;
        }
        
        .scrollable-table-container::-webkit-scrollbar-thumb,
        .scrollable-container::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.5);
          border-radius: 3px;
          transition: background 0.2s ease;
        }
        
        .scrollable-table-container::-webkit-scrollbar-thumb:hover,
        .scrollable-container::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.8);
        }
        
        .scrollable-table-container::-webkit-scrollbar-corner,
        .scrollable-container::-webkit-scrollbar-corner {
          background: transparent;
        }
        
        /* Firefox scrollbar styles */
        .scrollable-table-container,
        .scrollable-container {
          scrollbar-width: thin;
          scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
        }
          }
          
          .charts-row > div {
            min-width: auto !important;
          }
        }
        
        /* Chart container responsive adjustments */
        @media (max-width: 1024px) {
          .chart-container {
            min-width: auto !important;
          }
        }
      `}</style>
    </>
  );
};

const mapStateToProps = (state) => ({
  // Add any Redux state mappings if needed
});

const mapDispatchToProps = (dispatch) => ({
  // Add any Redux dispatch mappings if needed
});

export default connect(mapStateToProps, mapDispatchToProps)(NanoDashboard);